import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

export const BOTTOM_SHEET_TRANSITION_MS = 320;

/** Mínimo de arrasto (px) para fechar ao soltar */
const DRAG_DISMISS_MIN_PX = 100;
/** Arrasto além disso fecha mesmo com movimento rápido curto */
const DRAG_DISMISS_RATIO = 0.2;

export type BottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Indicador (pill) no topo */
  showHandle?: boolean;
  /** Classes extras no container do painel */
  panelClassName?: string;
  id?: string;
  role?: React.AriaRole;
  'aria-modal'?: boolean;
  'aria-labelledby'?: string;
  /** Arrastar o indicador para baixo para fechar (padrão: true) */
  enableDragToDismiss?: boolean;
};

/**
 * Bottom sheet com overlay, portal e animação de subir/descer (320ms).
 * Opcional: arrastar a alça para baixo para fechar.
 */
export function BottomSheet({
  isOpen,
  onClose,
  children,
  showHandle = true,
  panelClassName,
  id,
  role = 'dialog',
  'aria-modal': ariaModal = true,
  'aria-labelledby': ariaLabelledBy,
  enableDragToDismiss = true,
}: BottomSheetProps) {
  const [rendered, setRendered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragYRef = useRef(0);
  const dragStartClientY = useRef(0);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    dragYRef.current = dragY;
  }, [dragY]);

  useEffect(() => {
    if (!isOpen) {
      setDragY(0);
      setIsDragging(false);
      isDraggingRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setRendered(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(raf);
    }

    setVisible(false);
    closeTimerRef.current = setTimeout(() => {
      setRendered(false);
      closeTimerRef.current = null;
    }, BOTTOM_SHEET_TRANSITION_MS);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [isOpen]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!enableDragToDismiss || !visible) return;
    e.preventDefault();
    dragStartClientY.current = e.clientY;
    isDraggingRef.current = true;
    setIsDragging(true);
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    const delta = e.clientY - dragStartClientY.current;
    const next = Math.max(0, delta);
    setDragY(next);
  };

  const endDrag = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    const y = dragYRef.current;
    const threshold = Math.max(
      DRAG_DISMISS_MIN_PX,
      typeof window !== 'undefined' ? window.innerHeight * DRAG_DISMISS_RATIO : DRAG_DISMISS_MIN_PX,
    );
    if (y >= threshold) {
      setDragY(0);
      onClose();
      return;
    }
    setDragY(0);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    endDrag();
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    isDraggingRef.current = false;
    setIsDragging(false);
    setDragY(0);
  };

  if (!rendered) return null;

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const overlayOpacity =
    visible && dragY > 0 ? Math.max(0.12, 1 - dragY / (vh * 0.45)) : visible ? 1 : 0;

  const panelTransform = visible ? `translateY(${dragY}px)` : 'translateY(100%)';
  const panelTransition = isDragging
    ? 'none'
    : !visible
      ? `transform ${BOTTOM_SHEET_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`
      : dragY > 0
        ? `transform 0.22s cubic-bezier(0.25, 0.1, 0.25, 1)`
        : `transform ${BOTTOM_SHEET_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;

  return createPortal(
    <>
      <div
        className={cn(
          'fixed inset-0 z-[9999] bg-black/30 ease-out',
          visible ? 'pointer-events-auto' : 'pointer-events-none opacity-0',
        )}
        style={{
          transitionDuration: isDragging ? '0ms' : `${BOTTOM_SHEET_TRANSITION_MS}ms`,
          opacity: visible ? overlayOpacity : 0,
        }}
        onClick={onClose}
        aria-hidden
      />
      <div
        id={id}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[10000] flex max-h-[85vh] flex-col rounded-t-xl border-t border-slate-200 bg-white text-gray-900 shadow-lg will-change-transform',
          panelClassName,
        )}
        style={{
          transform: panelTransform,
          transition: panelTransition,
        }}
        role={role}
        aria-modal={ariaModal}
        aria-labelledby={ariaLabelledBy}
      >
        {showHandle && enableDragToDismiss && (
          <div
            className="flex shrink-0 cursor-grab touch-none flex-col items-center select-none pb-2 pt-2 active:cursor-grabbing"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            role="presentation"
            aria-label="Arrastar para fechar"
          >
            <div className="h-1 w-10 shrink-0 rounded-full bg-slate-200" aria-hidden />
          </div>
        )}
        {showHandle && !enableDragToDismiss && (
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-slate-200" aria-hidden />
        )}
        {!showHandle && enableDragToDismiss && (
          <div
            className="h-10 shrink-0 cursor-grab touch-none select-none active:cursor-grabbing"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            role="presentation"
            aria-label="Arrastar para fechar"
          />
        )}
        {children}
      </div>
    </>,
    document.getElementById('portal-root') || document.body,
  );
}
